import { useEffect, useRef, useState } from 'react';
import { Mail, X, CheckCircle2, AlertTriangle, Clock } from 'lucide-react';
import { getEmailQueueStatus, type EmailQueueStatus } from '../services/admin_api';

// Premium-looking floating progress card that polls the email queue.
//
// Visibility rules:
//   - Hidden by default (status idle = total == 0).
//   - Auto-shows when a batch becomes active (in_progress = true).
//   - Stays visible briefly (5s) after completion so the admin sees the
//     final tally, then auto-dismisses unless the user dismissed manually.
//
// Polling:
//   - 2s while in_progress (responsive feel without flooding the API).
//   - 30s while idle (cheap heartbeat in case a new batch starts from
//     another admin tab). Stops entirely when the user dismisses.
const POLL_ACTIVE_MS = 2000;
const POLL_IDLE_MS = 30000;
const AUTO_HIDE_AFTER_DONE_MS = 5000;

function formatETA(seconds: number): string {
  if (seconds <= 0) return 'almost done';
  if (seconds < 60) return `~${seconds}s remaining`;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  if (m < 60) return `~${m}m ${s}s remaining`;
  const h = Math.floor(m / 60);
  return `~${h}h ${m % 60}m remaining`;
}

export function EmailQueueDashboard() {
  const [status, setStatus] = useState<EmailQueueStatus | null>(null);
  const [dismissed, setDismissed] = useState(false);
  const [visible, setVisible] = useState(false);
  const hideTimerRef = useRef<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    let timerId: number | null = null;

    const tick = async () => {
      try {
        const s = await getEmailQueueStatus();
        if (cancelled) return;
        setStatus(s);

        // Decide visibility based on the latest snapshot. Manual dismiss
        // wins until the NEXT batch starts (total resets when a new
        // batch begins on the backend).
        if (s.in_progress) {
          setDismissed(false);
          setVisible(true);
          if (hideTimerRef.current) {
            window.clearTimeout(hideTimerRef.current);
            hideTimerRef.current = null;
          }
        } else if (s.total > 0 && visible && !hideTimerRef.current) {
          // Finished — keep card visible briefly, then auto-hide.
          hideTimerRef.current = window.setTimeout(() => {
            setVisible(false);
            hideTimerRef.current = null;
          }, AUTO_HIDE_AFTER_DONE_MS);
        }
      } catch {
        // Soft-fail — backend may be transiently unreachable. Keep last
        // snapshot rendered; next tick will recover.
      } finally {
        if (!cancelled) {
          const delay = (status?.in_progress) ? POLL_ACTIVE_MS : POLL_IDLE_MS;
          timerId = window.setTimeout(tick, delay);
        }
      }
    };

    tick();

    return () => {
      cancelled = true;
      if (timerId) window.clearTimeout(timerId);
      if (hideTimerRef.current) window.clearTimeout(hideTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (dismissed || !visible || !status || status.total === 0) return null;

  const completed = status.sent + status.failed;
  const pct = status.total > 0 ? Math.min(100, Math.round((completed / status.total) * 100)) : 0;
  const isDone = !status.in_progress && status.total > 0;

  return (
    <div
      className="fixed bottom-6 right-6 z-50 w-[380px] overflow-hidden rounded-xl bg-white shadow-2xl ring-1 ring-black/5 transition-all duration-300"
      style={{ animation: 'slideUp 0.3s ease-out' }}
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-3 bg-gradient-to-br from-indigo-600 via-blue-600 to-cyan-600 px-5 py-4 text-white">
        <div className="flex items-center gap-3">
          <div className="rounded-lg bg-white/15 p-2 backdrop-blur-sm">
            {isDone
              ? <CheckCircle2 size={20} />
              : <Mail size={20} className="animate-pulse" />}
          </div>
          <div>
            <div className="text-sm font-semibold">
              {isDone ? 'Welcome Emails Sent' : 'Sending Welcome Emails'}
            </div>
            <div className="text-xs text-white/75">
              {isDone
                ? `${status.sent} delivered · ${status.failed} failed`
                : `${completed} of ${status.total} processed`}
            </div>
          </div>
        </div>
        <button
          onClick={() => setDismissed(true)}
          aria-label="Dismiss"
          className="rounded-md p-1 text-white/70 transition hover:bg-white/10 hover:text-white"
        >
          <X size={16} />
        </button>
      </div>

      {/* Progress */}
      <div className="px-5 py-4">
        <div className="mb-2 h-2 overflow-hidden rounded-full bg-slate-100">
          <div
            className="h-full rounded-full bg-gradient-to-r from-indigo-500 via-blue-500 to-cyan-500 transition-[width] duration-500 ease-out"
            style={{ width: `${pct}%` }}
          />
        </div>
        <div className="flex items-center justify-between text-xs">
          <span className="font-semibold text-slate-700">{pct}% complete</span>
          {!isDone && (
            <span className="flex items-center gap-1 text-slate-500">
              <Clock size={12} />
              {formatETA(status.eta_seconds)}
            </span>
          )}
        </div>

        {/* Stats grid */}
        <div className="mt-4 grid grid-cols-3 gap-2 text-center">
          <div className="rounded-lg bg-emerald-50 px-2 py-2">
            <div className="text-base font-bold text-emerald-700">{status.sent}</div>
            <div className="text-[10px] uppercase tracking-wide text-emerald-600/80">Sent</div>
          </div>
          <div className="rounded-lg bg-slate-50 px-2 py-2">
            <div className="text-base font-bold text-slate-700">{status.pending}</div>
            <div className="text-[10px] uppercase tracking-wide text-slate-500">Pending</div>
          </div>
          <div className={`rounded-lg px-2 py-2 ${status.failed > 0 ? 'bg-rose-50' : 'bg-slate-50'}`}>
            <div className={`text-base font-bold ${status.failed > 0 ? 'text-rose-700' : 'text-slate-400'}`}>
              {status.failed}
            </div>
            <div className={`text-[10px] uppercase tracking-wide ${status.failed > 0 ? 'text-rose-600/80' : 'text-slate-400'}`}>
              Failed
            </div>
          </div>
        </div>

        {/* Footer note */}
        {!isDone && (
          <p className="mt-3 text-[11px] leading-relaxed text-slate-500">
            Throughput: {status.throughput_per_sec.toFixed(1)} emails/sec ·
            Safe for SES rate limits.
          </p>
        )}
        {isDone && status.failed > 0 && (
          <p className="mt-3 flex items-start gap-1.5 text-[11px] leading-relaxed text-rose-600">
            <AlertTriangle size={12} className="mt-px shrink-0" />
            {status.failed} email{status.failed !== 1 ? 's' : ''} failed after retries. Check server logs for details.
          </p>
        )}
      </div>

      <style>{`
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(20px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}
